import React, { useMemo, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { orderBy, limit } from 'firebase/firestore';
import {
  Card,
  Table,
  Badge,
  Input,
  Pagination,
  CsvExportButton,
} from '../../components/UI';
import { useFirestoreCollection, usePagination } from '../../hooks';
import styles from './AuditLog.module.css';

// adminLogs.{ action, actorUid, actorEmail, targetId, ..., createdAt }
// Written by Cloud Functions only (rules: client read-only).

const formatDate = (value) => {
  if (!value) return '-';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString();
};

const knownActions = [
  { value: 'all', label: 'All' },
  { value: 'grant_admin', label: 'Grant admin' },
  { value: 'revoke_admin', label: 'Revoke admin' },
  { value: 'ban_user', label: 'Ban user' },
  { value: 'unban_user', label: 'Unban user' },
  { value: 'moderate_report', label: 'Moderate report' },
  { value: 'delete_post', label: 'Delete post' },
  { value: 'resolve_missing_pet', label: 'Resolve missing pet' },
  { value: 'delete_missing_pet', label: 'Delete missing pet' },
  { value: 'update_order_status', label: 'Update order status' },
  { value: 'admin_login', label: 'Admin login' },
];

const prettifyAction = (action) => {
  if (!action) return '-';
  const known = knownActions.find((a) => a.value === action);
  if (known) return known.label;
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const variantForAction = (action) => {
  if (!action) return 'inactive';
  if (action.startsWith('grant') || action.startsWith('resolve') || action === 'unban_user') return 'success';
  if (action.startsWith('delete') || action === 'ban_user' || action === 'revoke_admin') return 'danger';
  if (action.includes('order') || action.includes('moderate')) return 'primary';
  return 'secondary';
};

const AuditLog = () => {
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const constraints = useMemo(() => [orderBy('createdAt', 'desc'), limit(1000)], []);
  const { data: logs, loading, error } = useFirestoreCollection('adminLogs', constraints);

  const filtered = useMemo(() => {
    let list = logs;
    if (actionFilter !== 'all') list = list.filter((l) => l.action === actionFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((l) =>
        Object.values(l).some(
          (v) => typeof v === 'string' && v.toLowerCase().includes(q)
        )
      );
    }
    return list;
  }, [logs, actionFilter, searchQuery]);

  const { page, pageSize, pageCount, pageData, totalItems, setPage, setPageSize } =
    usePagination(filtered, 50);

  const csvColumns = [
    { key: 'createdAt', label: 'Time', accessor: (l) => formatDate(l.createdAt) },
    { key: 'action', label: 'Action' },
    { key: 'actorEmail', label: 'Admin' },
    { key: 'actorUid', label: 'Admin UID' },
    { key: 'targetId', label: 'Target ID' },
    { key: 'reason', label: 'Reason / Notes', accessor: (l) => l.reason || l.notes || '' },
  ];

  const columns = [
    {
      key: 'createdAt',
      title: 'Time',
      render: (v) => formatDate(v),
    },
    {
      key: 'action',
      title: 'Action',
      render: (v) => <Badge variant={variantForAction(v)}>{prettifyAction(v)}</Badge>,
    },
    {
      key: 'actorEmail',
      title: 'Admin',
      render: (v, row) => v || row.actorUid || '-',
    },
    {
      key: 'targetId',
      title: 'Target',
      render: (v, row) => v || row.reportId || row.targetUid || '-',
    },
    {
      key: 'details',
      title: 'Details',
      render: (_, row) => {
        const parts = [];
        if (row.targetEmail) parts.push(`email: ${row.targetEmail}`);
        if (row.moderationAction) parts.push(`moderation: ${row.moderationAction}`);
        if (row.newStatus) parts.push(`status → ${row.newStatus}`);
        if (row.reason) parts.push(`reason: ${row.reason}`);
        if (row.notes) parts.push(`notes: ${row.notes}`);
        return parts.length ? parts.join(' · ') : '-';
      },
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>Audit Log</h1>
          {!loading && <Badge variant="primary">{logs.length} entries</Badge>}
        </div>
        <p className={styles.subtitle}>
          Every admin action across the platform
          <span className={styles.liveText}> Live</span>
        </p>
      </div>

      {error && (
        <Card>
          <div style={{ color: '#f87171' }}>Error loading audit log: {error.message}</div>
        </Card>
      )}

      <Card>
        <div className={styles.toolbar}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Search action, actor, target..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={FiSearch}
            />
          </div>
          <div className={styles.filters}>
            {knownActions.map((a) => (
              <button
                key={a.value}
                className={`${styles.filterBtn} ${actionFilter === a.value ? styles.active : ''}`}
                onClick={() => setActionFilter(a.value)}
                title={a.value}
              >
                {a.label}
              </button>
            ))}
          </div>
          <CsvExportButton rows={filtered} columns={csvColumns} filename="dierend-audit-log" />
        </div>

        <Table columns={columns} data={pageData} loading={loading} emptyMessage="No audit entries" />

        <Pagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[25, 50, 100, 200]}
        />
      </Card>
    </div>
  );
};

export default AuditLog;
